import type { RoleName, Permission, RouteProtection } from '../types/rbac';

// Default route paths
export const ROUTE_PATHS = {
    LOGIN: '/login',
    DASHBOARD: '/dashboard',
    ACCESS_DENIED: '/access-denied',
    HOME: '/',
} as const;

// Default protection configurations for common scenarios
export const PROTECTION_PRESETS: Record<string, RouteProtection> = {
    // Require authentication only
    AUTHENTICATED: {
        requireAuth: true,
        fallbackPath: ROUTE_PATHS.LOGIN,
    },

    // Admin only access
    ADMIN_ONLY: {
        requireAuth: true,
        requiredRoles: ['admin'],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    },

    // Editor or Admin access
    EDITOR_OR_ADMIN: {
        requireAuth: true,
        requiredRoles: ['editor', 'admin'],
        requireAllRoles: false,
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    },

    // User management permissions
    USER_MANAGEMENT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'user', action: 'list' },
        ],
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    },

    // Project management permissions
    PROJECT_MANAGEMENT: {
        requireAuth: true,
        requiredPermissions: [
            { resource: 'project', action: 'create' },
            { resource: 'project', action: 'update' },
        ],
        requireAllPermissions: false,
        fallbackPath: ROUTE_PATHS.ACCESS_DENIED,
    },

    // Public access (no restrictions)
    PUBLIC: {},
} as const;

// Error messages for different access scenarios
export const ACCESS_ERROR_MESSAGES = {
    AUTHENTICATION_REQUIRED: 'You must be logged in to access this page.',
    INSUFFICIENT_PERMISSIONS: 'You do not have permission to access this page.',
    INVALID_ROLE: 'Your account role is not valid for this action.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
    ROLE_REQUIRED: (roles: RoleName[]) =>
        `This page requires one of the following roles: ${roles.join(', ')}.`,
    PERMISSION_REQUIRED: (permissions: Permission[]) =>
        `This page requires the following permissions: ${permissions.map(p => `${p.resource}:${p.action}`).join(', ')}.`,
} as const;

// Loading messages
export const LOADING_MESSAGES = {
    AUTHENTICATING: 'Authenticating...',
    CHECKING_PERMISSIONS: 'Checking permissions...',
    LOADING_SESSION: 'Loading session...',
    REFRESHING_TOKEN: 'Refreshing authentication...',
} as const;

// Cache configuration
export const CACHE_CONFIG = {
    PERMISSION_TTL: 5 * 60 * 1000, // 5 minutes
    ROLE_TTL: 10 * 60 * 1000, // 10 minutes
    SESSION_REFRESH_INTERVAL: 15 * 60 * 1000, // 15 minutes
} as const;

// Role display names and descriptions
export const ROLE_METADATA: Record<RoleName, { displayName: string; description: string; color: string }> = {
    admin: {
        displayName: 'Administrator',
        description: 'Full system access with all permissions',
        color: 'red',
    },
    editor: {
        displayName: 'Editor',
        description: 'Can create and edit projects',
        color: 'blue',
    },
    user: {
        displayName: 'User',
        description: 'Basic access to view projects',
        color: 'green',
    },
} as const;

// Permission display metadata
export const PERMISSION_METADATA: Record<string, { displayName: string; description: string }> = {
    'user:create': {
        displayName: 'Create Users',
        description: 'Can create new user accounts',
    },
    'user:list': {
        displayName: 'List Users',
        description: 'Can view list of users',
    },
    'user:set-role': {
        displayName: 'Set User Roles',
        description: 'Can assign roles to users',
    },
    'user:ban': {
        displayName: 'Ban Users',
        description: 'Can ban user accounts',
    },
    'user:impersonate': {
        displayName: 'Impersonate Users',
        description: 'Can impersonate other users',
    },
    'user:delete': {
        displayName: 'Delete Users',
        description: 'Can delete user accounts',
    },
    'user:set-password': {
        displayName: 'Set User Passwords',
        description: 'Can set passwords for users',
    },
    'project:create': {
        displayName: 'Create Projects',
        description: 'Can create new projects',
    },
    'project:view': {
        displayName: 'View Projects',
        description: 'Can view project details',
    },
    'project:update': {
        displayName: 'Update Projects',
        description: 'Can edit project information',
    },
    'project:delete': {
        displayName: 'Delete Projects',
        description: 'Can delete projects',
    },
} as const;

// Development/debugging flags
export const DEBUG_FLAGS = {
    LOG_PERMISSION_CHECKS: process.env.NODE_ENV === 'development',
    LOG_ROLE_CHANGES: process.env.NODE_ENV === 'development',
    LOG_ROUTE_PROTECTION: process.env.NODE_ENV === 'development',
} as const;